import { useCallback, useRef } from 'react';
import { type GestureResponderEvent, Platform } from 'react-native';
import {
    Slider as TamaguiSlider,
    type SliderProps as TamaguiSliderProps,
} from 'tamagui';

import type { ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { Shadows } from '../Shadows';

interface SliderProps extends TamaguiSliderProps {
    tone?: 'positive' | 'negative' | 'neutral' | 'warning';
    trackColor?: ColorTokenKey;
    trackActiveColor?: ColorTokenKey;
    thumbColor?: ColorTokenKey;
    thumbSize?: string | number;
}

const getActiveColor = (tone: SliderProps['tone']) => {
    switch (tone) {
        case 'positive':
            return '$green1000';
        case 'negative':
            return '$red900';
        case 'warning':
            return '$orange1000';
        case 'neutral':
        default:
            return '$gray1200';
    }
};

export const Slider = ({
    trackColor = '$gray400',
    thumbColor = '$white',
    tone = 'neutral',
    onSlideMove,
    onSlideEnd,
    min = 0,
    max = 100,
    trackActiveColor,
    thumbSize = '$2.75',
    ...props
}: SliderProps) => {
    const thumbRef = useRef<HTMLElement>(null);

    // On web, the browser leaves focus on the thumb after a pointer drag,
    // which keeps Tamagui's hover/focus background style applied (gray).
    // Blurring the thumb on slide end clears that stale focus state.
    const handleSlideEnd = useCallback(
        (e: GestureResponderEvent, value: number) => {
            onSlideEnd?.(e, value);
            if (Platform.OS === 'web') {
                thumbRef.current?.blur();
            }
        },
        [onSlideEnd],
    );

    return (
        <TamaguiSlider
            size="$4"
            width={200}
            defaultValue={[50]}
            min={min}
            max={max}
            step={1}
            onSlideMove={(e, value: number) => {
                // manually applying max and min because otherwise the thumb can be moved beyond the bounds
                onSlideMove?.(e, Math.max(min, Math.min(value, max)));
            }}
            onSlideEnd={handleSlideEnd}
            {...props}
        >
            <TamaguiSlider.Track backgroundColor={trackColor} borderRadius={4}>
                <TamaguiSlider.TrackActive
                    backgroundColor={trackActiveColor ?? getActiveColor(tone)}
                    borderRadius={4}
                />
            </TamaguiSlider.Track>
            <TamaguiSlider.Thumb
                ref={thumbRef}
                circular
                index={0}
                size={thumbSize}
                borderWidth={1}
                borderColor="$gray300"
                backgroundColor={thumbColor}
                hoverStyle={{
                    backgroundColor: '$gray200',
                }}
                {...Shadows.SMALL}
            />
        </TamaguiSlider>
    );
};
